<template>
  <v-select
    v-model="selectedCurrency"
    :items="currencies"
    :label="label"
    :placeholder="placeholder"
    :clearable="clearable"
    :disabled="disabled"
    :loading="loading"
    :hint="hint"
    :persistent-hint="persistentHint"
    item-value="code"
    @change="handleChange"
  >
    <template v-slot:item="{ item }">
      {{ item.code }} - {{ item.name }}
    </template>

    <template v-slot:selection="{ item }">
      {{ item.code }} - {{ item.name }}
    </template>
  </v-select>
</template>

<script>
// Module-level cache to avoid redundant API calls across multiple instances
let cachedCurrencies = null;

export default {
  name: 'CurrencySelect',

  props: {
    value: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: 'Currency'
    },
    placeholder: {
      type: String,
      default: 'Select currency'
    },
    clearable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: ''
    },
    persistentHint: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedCurrency: null,
      currencies: [],
      loading: false
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.selectedCurrency = newVal;
      }
    }
  },

  mounted() {
    this.loadCurrencies();
  },

  methods: {
    async loadCurrencies() {
      if (cachedCurrencies) {
        this.currencies = cachedCurrencies;
        if (this.value) this.selectedCurrency = this.value;
        return;
      }
      this.loading = true;
      try {
        const response = await this.$axios.get('/api/v1/currencies');
        if (response.data && response.data.data) {
          const data = response.data.data;
          cachedCurrencies = data.currencies;
          this.currencies = data.currencies;

          // Set initial selection if value is provided
          if (this.value) {
            this.selectedCurrency = this.value;
          }
        }
      } catch (error) {
        console.error('Failed to load currencies:', error);
        this.$store.dispatch('showMessage', {
          message: 'Failed to load currencies',
          color: 'error'
        });

        // Fallback to common currencies
        this.currencies = [
          // Major currencies
          { code: 'USD', name: 'US Dollar', symbol: '$', decimals: 2 },
          { code: 'EUR', name: 'Euro', symbol: '€', decimals: 2 },
          { code: 'GBP', name: 'British Pound', symbol: '£', decimals: 2 },
          { code: 'JPY', name: 'Japanese Yen', symbol: '¥', decimals: 0 },
          { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', decimals: 2 },
          { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', decimals: 2 },

          // Common trading currencies
          { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$', decimals: 2 },
          { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', decimals: 2 },
          { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', decimals: 2 },
          { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', decimals: 2 },
          { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', decimals: 2 },
          { code: 'KRW', name: 'South Korean Won', symbol: '₩', decimals: 0 },
          { code: 'INR', name: 'Indian Rupee', symbol: '₹', decimals: 2 },
          { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$', decimals: 2 },
          { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', decimals: 2 },
          { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', decimals: 2 },
          { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', decimals: 2 },
          { code: 'DKK', name: 'Danish Krone', symbol: 'kr', decimals: 2 },
          { code: 'PLN', name: 'Polish Złoty', symbol: 'zł', decimals: 2 },
          { code: 'THB', name: 'Thai Baht', symbol: '฿', decimals: 2 },
          { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', decimals: 2 },
          { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', decimals: 2 },
          { code: 'PHP', name: 'Philippine Peso', symbol: '₱', decimals: 2 },
          { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', decimals: 0 },
          { code: 'ZAR', name: 'South African Rand', symbol: 'R', decimals: 2 },
          { code: 'AED', name: 'UAE Dirham', symbol: 'AED', decimals: 2 },
          { code: 'SAR', name: 'Saudi Riyal', symbol: 'SAR', decimals: 2 },
          { code: 'TRY', name: 'Turkish Lira', symbol: '₺', decimals: 2 }
        ];
      } finally {
        this.loading = false;
      }
    },

    handleChange(currencyCode) {
      this.$emit('input', currencyCode);
      const currency = this.currencies.find(c => c.code === currencyCode);
      this.$emit('change', currency);
    }
  }
};
</script>

<style scoped>
/* Add any custom styles if needed */
</style>